import React from "react";
import { RowData } from "../types";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import { InvoicePDF } from "./InvoicePDF";

/**
 * Generates a PDF file for a single invoice
 * @param rowData The invoice data
 * @returns Promise containing the PDF blob
 */
export const generateInvoicePDF = async (rowData: RowData): Promise<Blob> => {
  // Pass the data directly to the PDF component
  return await pdf(InvoicePDF({ rowData })).toBlob();
};

/**
 * Generates and saves a single invoice PDF
 * @param rowData The invoice data
 * @returns Promise that resolves when the PDF is saved
 */
export const downloadSingleInvoice = async (rowData: RowData): Promise<void> => {
  try {
    const blob = await generateInvoicePDF(rowData);
    const fileName = formatInvoiceFileName(rowData);
    saveAs(blob, fileName);
    return Promise.resolve();
  } catch (error) {
    console.error("Error generating PDF:", error);
    return Promise.reject(error);
  }
};

/**
 * Generates a PDF preview URL for the given invoice data
 * @param rowData The invoice data
 * @returns Promise containing the preview URL
 */
export const createPDFPreviewUrl = async (rowData: RowData): Promise<string> => {
  try {
    const blob = await generateInvoicePDF(rowData);
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error("Error creating PDF preview:", error);
    return Promise.reject(error);
  }
};

/**
 * Creates a ZIP file containing all invoices
 * @param rowsData Array of invoice data
 * @returns Promise containing the ZIP blob
 */
export const createInvoicesZip = async (rowsData: RowData[]): Promise<Blob> => {
  try {
    const zip = new JSZip();
    
    // Generate all PDFs
    const pdfPromises = rowsData.map(async (rowData) => {
      const blob = await generateInvoicePDF(rowData);
      return {
        name: formatInvoiceFileName(rowData),
        blob
      };
    });
    
    const pdfFiles = await Promise.all(pdfPromises);
    
    // Add each PDF to the zip file
    pdfFiles.forEach(file => {
      zip.file(file.name, file.blob);
    });
    
    // Generate the zip file
    return await zip.generateAsync({ type: "blob" });
  } catch (error) {
    console.error("Error generating zip file:", error);
    return Promise.reject(error);
  }
};

/**
 * Downloads a ZIP file containing all invoices
 * @param rowsData Array of invoice data
 * @returns Promise that resolves when the ZIP is saved
 */
export const downloadAllInvoices = async (rowsData: RowData[]): Promise<void> => {
  try {
    const zipBlob = await createInvoicesZip(rowsData);
    saveAs(zipBlob, "All_Invoices.zip");
    return Promise.resolve();
  } catch (error) {
    console.error("Error downloading all invoices:", error);
    return Promise.reject(error);
  }
};

/**
 * Formats a consistent file name for invoice PDFs
 * @param rowData The invoice data
 * @returns Formatted file name
 */
export const formatInvoiceFileName = (rowData: RowData): string => {
  const expertId = rowData.expert_id || rowData.id.toString().padStart(6, '0');
  const expertName = rowData.expert_name || 'invoice';
  const sanitizedName = expertName.replace(/[^a-zA-Z0-9]/g, '_');
  
  return `${sanitizedName}_${expertId}.pdf`;
};

/**
 * Formats a date for display in the invoice
 * @param dateString The date string to format
 * @returns Formatted date string
 */
export const formatInvoiceDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString();
};

/**
 * Generates an invoice number from the row data
 * @param rowData The invoice data
 * @returns Formatted invoice number
 */
export const getInvoiceNumber = (rowData: RowData): string => {
  return rowData.invoice_no || rowData.id.toString().padStart(6, '0');
};